/* R148d · Feb 2026 — Subtask preview button
 * Small clickable button rendered next to a task title in list views.
 * Shows the total subtask count as a chip; on click opens a lightweight
 * popover with each subtask (title, status, assignee, due date).
 * Popover items are clickable to open the full subtask detail. */
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { ListTree, CheckCircle2, Circle, User, Calendar } from "lucide-react";

const STATUS_ICON = {
  done: <CheckCircle2 className="w-3 h-3 text-emerald-600" />,
  in_progress: <Circle className="w-3 h-3 text-sky-500 fill-sky-500/30" />,
  review: <Circle className="w-3 h-3 text-amber-500 fill-amber-500/30" />,
  blocked: <Circle className="w-3 h-3 text-red-500 fill-red-500/30" />,
  cancelled: <Circle className="w-3 h-3 text-muted-foreground opacity-40" />,
  open: <Circle className="w-3 h-3 text-muted-foreground" />,
};

export default function SubtaskPreviewButton({ task, memberName, onOpenSubtask }) {
  const count = task.subtask_count || 0;
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setBusy(true);
    api.get(`/tasks/${task.id}/subtasks`)
      .then((r) => { if (alive) setSubs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setSubs([]); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [open, task.id]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!btnRef.current) return;
      if (btnRef.current.contains(e.target)) return;
      // Popover clicks won't propagate through the portal element check;
      // guard by checking class.
      if (e.target.closest?.("[data-subtask-preview-popover]")) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      // Position popover below the button, aligned to left edge; clamp to viewport.
      const width = 320;
      let left = r.left;
      if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
      setPos({ top: r.bottom + 4, left });
    }
    setOpen((v) => !v);
  };

  if (count === 0) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 border border-border bg-muted/40 hover:bg-muted/70 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground rounded-none"
        title={`${count} subtask${count === 1 ? "" : "s"} — click to preview`}
        data-testid={`subtask-preview-btn-${task.id}`}
      >
        <ListTree className="w-3 h-3" />
        {count}
      </button>
      {open && createPortal(
        <div
          data-subtask-preview-popover
          className="fixed z-[80] w-[320px] max-h-[400px] overflow-y-auto border border-border bg-card shadow-lg"
          style={{ top: pos.top, left: pos.left }}
          data-testid={`subtask-preview-popover-${task.id}`}
        >
          <div className="px-3 py-2 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center justify-between">
            <span>Subtasks · {count}</span>
            <span className="text-[9px] normal-case tracking-normal">{subs.filter((s) => s.status === "done").length} done</span>
          </div>
          {busy && subs.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Loading…</div>
          ) : subs.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">No subtasks.</div>
          ) : (
            <ul className="divide-y divide-border">
              {subs.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpen(false); onOpenSubtask?.(s); }}
                    className="w-full text-left px-3 py-2 hover:bg-muted/30 flex items-start gap-2"
                    data-testid={`subtask-preview-row-${s.id}`}
                  >
                    <span className="mt-0.5">{STATUS_ICON[s.status] || STATUS_ICON.open}</span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs ${s.status === "done" ? "line-through text-muted-foreground" : "font-medium"}`}>
                        {s.title}
                      </div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
                        {s.assignee_id && (
                          <span className="inline-flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{memberName?.(s.assignee_id) || s.assignee_name || "assigned"}</span>
                        )}
                        {s.due_date && (
                          <span className="inline-flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{s.due_date}</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
